import { useEffect, useMemo } from "react";

import {
  IFoundTrain,
  IMenuStatusUpdate,
  IMenuTreeItem,
  stationHasVisibleTrains,
  useAsu,
} from "@framework/provider-asu";

import { useSubscribe } from "./provider";
import { ISubscribeRequest, SubscribeCode, SubscribeListener } from "./types";

type MenuStatusSubscribeCode = SubscribeCode.STATUS_TOPLEVEL | SubscribeCode.STATUS_TRAIN;

export interface IMenuStatusSubscriptionRequest extends ISubscribeRequest {
  type: MenuStatusSubscribeCode;
}

type MenuSearchItem = IMenuTreeItem | IFoundTrain;

const getRequestKey = ({ type, id }: IMenuStatusSubscriptionRequest): string => `${type}:${id}`;

const normalizeRequests = (requests: readonly IMenuStatusSubscriptionRequest[]): IMenuStatusSubscriptionRequest[] => {
  const unique = new Map<string, IMenuStatusSubscriptionRequest>();

  for (const request of requests) {
    if (request.id > 0) {
      unique.set(getRequestKey(request), request);
    }
  }

  return [...unique.values()].sort((left, right) => left.type - right.type || left.id - right.id);
};

export const collectVisibleTreeStatusRequests = (
  stations: readonly IMenuTreeItem[],
  expandedNodeIds: ReadonlySet<string>,
): IMenuStatusSubscriptionRequest[] => {
  const requests: IMenuStatusSubscriptionRequest[] = [];

  const visitStations = (items: readonly IMenuTreeItem[]): void => {
    for (const station of items) {
      if (!stationHasVisibleTrains(station)) {
        continue;
      }

      requests.push({ type: SubscribeCode.STATUS_TOPLEVEL, id: station.Id });

      if (!expandedNodeIds.has(String(station.Id))) {
        continue;
      }

      for (const train of station.Trains) {
        if (train.TrainId && train.TrainName) {
          requests.push({ type: SubscribeCode.STATUS_TRAIN, id: train.TrainId });
        }
      }

      visitStations(station.children);
    }
  };

  visitStations(stations);
  return normalizeRequests(requests);
};

export const collectSearchStatusRequests = (items: readonly MenuSearchItem[]): IMenuStatusSubscriptionRequest[] => {
  const requests: IMenuStatusSubscriptionRequest[] = [];

  for (const item of items) {
    if ("TrainId" in item && item.TrainId && item.TrainName) {
      requests.push({ type: SubscribeCode.STATUS_TRAIN, id: item.TrainId });
    } else if ("Id" in item && item.Id) {
      requests.push({ type: SubscribeCode.STATUS_TOPLEVEL, id: item.Id });
    }
  }

  return normalizeRequests(requests);
};

export const useMenuStatusSubscriptions = (requests: readonly IMenuStatusSubscriptionRequest[]): void => {
  const { applySubscribeMenuStatusUpdates, isMenuOpen } = useAsu();
  const { connected, enabled, subscribe } = useSubscribe();
  const requestsKey = requests.map(getRequestKey).sort().join(",");
  const normalizedRequests = useMemo(() => normalizeRequests(requests), [requestsKey]);

  useEffect(() => {
    if (!enabled || !connected || !isMenuOpen || !normalizedRequests.length) {
      return;
    }

    let disposed = false;
    let scheduled = false;
    const pendingUpdates: IMenuStatusUpdate[] = [];

    const flushUpdates = (): void => {
      scheduled = false;
      if (disposed || !pendingUpdates.length) {
        return;
      }

      applySubscribeMenuStatusUpdates(pendingUpdates.splice(0));
    };

    const handleMessages: SubscribeListener = messages => {
      for (const message of messages) {
        if (!("ObjectId" in message)) {
          continue;
        }

        pendingUpdates.push({
          objectId: message.ObjectId,
          objectType: message.SubscribeType === SubscribeCode.STATUS_TOPLEVEL ? "station" : "train",
          alarmStatus: message.AlarmStatus,
          date: message.Date,
        });
      }

      if (pendingUpdates.length && !scheduled) {
        scheduled = true;
        void Promise.resolve().then(flushUpdates);
      }
    };

    const unsubscribe = normalizedRequests.map(request => subscribe(request, handleMessages));

    return () => {
      disposed = true;
      pendingUpdates.length = 0;
      unsubscribe.forEach(stop => stop());
    };
  }, [applySubscribeMenuStatusUpdates, connected, enabled, isMenuOpen, normalizedRequests, subscribe]);
};
